import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import api, { productsAPI, conversationsAPI, reviewsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import StarRating from '../components/StarRating';
import SellerReviews from '../components/SellerReviews';
import '../styles/AnnonceDetail.css';

const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, isInCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [sellerStats, setSellerStats] = useState(null);
  const [showReviews, setShowReviews] = useState(false);
  const [translatedTitle, setTranslatedTitle] = useState(null);
  const [translatedDesc, setTranslatedDesc] = useState(null);
  const [translating, setTranslating] = useState(false);

  const handleTranslate = async () => {
    if (translatedTitle) {
      setTranslatedTitle(null);
      setTranslatedDesc(null);
      return;
    }
    const lang = i18n.language === 'fr' ? 'en' : i18n.language;
    setTranslating(true);
    try {
      const textToTranslate = product.title + '\n---\n' + product.description;
      const res = await api.post('/translate', { text: textToTranslate, targetLang: lang });
      const parts = res.data.translatedText.split('\n---\n');
      setTranslatedTitle(parts[0] || res.data.translatedText);
      setTranslatedDesc(parts[1] || '');
    } catch (err) {
      console.error('Erreur traduction:', err);
    } finally {
      setTranslating(false);
    }
  };

  const conditionLabels = {
    'neuf_etiquette': t('conditions.neuf_etiquette'),
    'neuf_sans_etiquette': t('conditions.neuf_sans_etiquette'),
    'tres_bon': t('conditions.tres_bon'),
    'bon': t('conditions.bon'),
    'satisfaisant': t('conditions.satisfaisant')
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  /*
   * Le titre de l'onglet est celui de l'annonce, comme celui que le serveur
   * ecrit dans le HTML envoye (backend/server.js). PageTitle laisse la main
   * sur /product/ pour cette raison : la table de titres de App.js ne connait
   * que des chemins fixes, elle ne peut rien dire d'une annonce.
   *
   * Utile surtout apres une navigation interne, ou le titre du serveur est
   * reste celui de la page de depart.
   */
  useEffect(() => {
    if (product?.title) {
      document.title = `${product.title} | Main2Main`;
    }
  }, [product?.title]);

  // Fermer le zoom avec Echap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowZoom(false);
      } else if (showZoom && e.key === 'ArrowRight') {
        handleNextImage();
      } else if (showZoom && e.key === 'ArrowLeft') {
        handlePrevImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showZoom, currentImageIndex, product]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getById(id);
      const data = response.data;
      setProduct(data);

      // Utiliser les stats vendeur incluses dans la réponse produit
      if (data.seller_stats) {
        setSellerStats(data.seller_stats);
      } else if (data.user_id) {
        // Fallback: fetch séparé (non-bloquant)
        reviewsAPI.getSellerStats(data.user_id)
          .then(res => setSellerStats(res.data))
          .catch(() => {});
      }
    } catch (err) {
      setError(t('product.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await productsAPI.removeFromFavorites(id);
        setIsFavorite(false);
      } else {
        await productsAPI.addToFavorites(id);
        setIsFavorite(true);
      }
    } catch (err) {
      console.error('Erreur favoris:', err);
    }
  };

  const handleContact = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowContactModal(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!contactMessage.trim()) return;

    setSendingMessage(true);
    try {
      const response = await conversationsAPI.start(product.id, contactMessage);
      setShowContactModal(false);
      setContactMessage('');
      navigate(`/my-messages?conversation=${response.data.conversationId}`);
    } catch (err) {
      console.error('Erreur envoi message:', err);
      alert(t('product.messageError'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleNextImage = () => {
    if (product && product.images && product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const handlePrevImage = () => {
    if (product && product.images && product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const handleImageClick = () => {
    setShowZoom(true);
  };

  // Swipe tactile - minimum de distance pour declencher
  const minSwipeDistance = 50;
  const [swiped, setSwiped] = useState(false);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setSwiped(false);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextImage();
      setSwiped(true);
    } else if (isRightSwipe) {
      handlePrevImage();
      setSwiped(true);
    }
  };

  // Fermer le zoom seulement si on n'a pas swipe
  const handleZoomClose = () => {
    if (!swiped) {
      setShowZoom(false);
    }
    setSwiped(false);
  };

  // Determiner si c'est un vehicule
  const isVehicle = product && (product.parent_category_slug === 'voitures' || product.category_slug === 'voitures');

  // Frais dynamiques selon categorie
  const BUYER_PROTECTION = isVehicle ? 0 : 1.5;
  const SERVICE_FEE_RATE = isVehicle ? 0 : 0.02;

  // Calculer les frais de service
  const getServiceFee = () => {
    if (!product) return 0;
    return parseFloat(product.price) * SERVICE_FEE_RATE;
  };

  // Calculer le prix total avec frais
  const calculateTotalPrice = () => {
    if (!product) return 0;
    const price = parseFloat(product.price);
    return (price + BUYER_PROTECTION + getServiceFee()).toFixed(2);
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-container">
          <div className="product-gallery">
            <div className="main-image skeleton-image">
              <div className="skeleton-pulse" />
            </div>
          </div>
          <div className="product-info">
            <div className="skeleton-line skeleton-title" />
            <div className="skeleton-line skeleton-price" />
            <div className="skeleton-line skeleton-text" />
            <div className="skeleton-line skeleton-text short" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return <div className="error-container"><p>{error || t('product.notFound')}</p></div>;
  }

  const images = product.images || [];
  const currentImage = images.length > 0
    ? `/uploads/optimized/${images[currentImageIndex]}`
    : null;

  return (
    <div className="product-detail-page">
      <div className="product-detail-container">
        {/* Galerie d'images */}
        <div className="product-gallery">
          <div
            className="main-image"
            onClick={handleImageClick}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {currentImage ? (
              <>
                <img src={currentImage} alt={product.title} draggable="false" />
                <div className="zoom-hint">
                  <span>🔍</span> {t('product.clickToZoom')}
                </div>
                {/* Indicateur swipe sur mobile */}
                {images.length > 1 && (
                  <div className="swipe-hint">
                    <span>👆</span> {t('product.swipeForMore')}
                  </div>
                )}
              </>
            ) : (
              <div className="no-image">{t('product.noImage')}</div>
            )}
            {product.status === 'sold' && (
              <div className="sold-overlay">{t('product.soldOverlay')}</div>
            )}

            {/* Fleches navigation */}
            {images.length > 1 && (
              <>
                <button
                  className="gallery-nav gallery-prev"
                  onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                >
                  ‹
                </button>
                <button
                  className="gallery-nav gallery-next"
                  onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                >
                  ›
                </button>
              </>
            )}

            {/* Points indicateurs */}
            {images.length > 1 && (
              <div className="image-dots">
                {images.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                  />
                ))}
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img
                    src={`/uploads/thumbnails/${img}`}
                    alt={`Photo ${index + 1}`}
                    loading="lazy"
                    onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${img}`; }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Indicateur de photos */}
          {images.length > 1 && (
            <div className="image-counter">
              {currentImageIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Informations du produit */}
        <div className="product-info">
          {/* Titre */}
          <h1 className="product-title">{product.title}</h1>

          {/* Prix */}
          <div className="product-price-box">
            <div className="price-row">
              <span className="price-label">{t('product.totalPrice')}</span>
              <span className="price-total">{calculateTotalPrice()} CHF</span>
            </div>
            <div className="price-details">
              <span>{t('product.itemPrice', { price: parseFloat(product.price).toFixed(2) })}</span>
              {!isVehicle && <span>{t('product.protectionFee')}: {BUYER_PROTECTION.toFixed(2)} CHF</span>}
              {!isVehicle && <span>{t('product.serviceFee')}: {getServiceFee().toFixed(2)} CHF</span>}
            </div>
            {product.original_price && product.original_price > product.price && (
              <div className="price-original">
                <span className="original-price">{parseFloat(product.original_price).toFixed(2)} CHF</span>
                <span className="discount-badge">
                  -{Math.round((1 - product.price / product.original_price) * 100)}%
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="product-actions">
            {product.status === 'active' && (!user || user.userId !== product.user_id) && (
              <>
                {isVehicle ? (
                  <button className="btn-contact-vehicle" onClick={handleContact}>
                    {t('vehicle.contactSeller')}
                  </button>
                ) : (
                  <>
                    <button
                      className="btn-buy"
                      onClick={() => {
                        if (!user) {
                          navigate('/login');
                          return;
                        }
                        navigate(`/buy/${product.id}`);
                      }}
                    >
                      {t('product.buyButton')}
                    </button>
                    <button
                      className={`btn-add-cart ${isInCart(product.id) ? 'in-cart' : ''}`}
                      disabled={isInCart(product.id)}
                      onClick={() => {
                        if (!user) {
                          navigate('/login');
                          return;
                        }
                        addToCart(product);
                      }}
                    >
                      {isInCart(product.id) ? t('product.inCart') : t('product.addToCart')}
                    </button>
                  </>
                )}
              </>
            )}
            <button
              className={`btn-favorite ${isFavorite ? 'active' : ''}`}
              onClick={handleFavorite}
            >
              {isFavorite ? '❤️' : '🤍'}
            </button>
            <button
              className="btn-share-facebook"
              onClick={() => {
                const shareUrl = `https://main2main.ch/product/${product.id}`;
                const shareText = `📦 ${product.title}\n💰 CHF ${(product.price / 100).toFixed(2)}\n\n👉 ${shareUrl}`;
                navigator.clipboard.writeText(shareText).then(() => {
                  alert(t('product.linkCopiedForFacebook', 'Texte copié ! Collez-le sur la page Main2Main Facebook.'));
                  window.open('https://www.facebook.com/profile.php?id=100089217293297', '_blank');
                }).catch(() => {
                  window.open('https://www.facebook.com/profile.php?id=100089217293297', '_blank');
                  window.prompt(t('product.copyLink', 'Copiez ce texte :'), shareText);
                });
              }}
              title={t('product.shareOnFacebook', 'Partager sur Facebook')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
            <button
              className="btn-share-instagram"
              onClick={() => {
                const shareUrl = `https://main2main.ch/product/${product.id}`;
                const shareText = `🆕 Nouvelle annonce sur Main2Main !\n\n📦 ${product.title}\n💰 CHF ${(product.price / 100).toFixed(2)}\n\n👉 ${shareUrl}\n\n#main2main #occasion #suisse #lausanne #vente`;
                window.open('https://www.instagram.com/main2main.ch/', '_blank');
                navigator.clipboard.writeText(shareText).then(() => {
                  alert(t('product.linkCopiedForInstagram', 'Texte copié ! Collez-le dans votre publication Instagram.'));
                }).catch(() => {
                  window.prompt(t('product.copyLink', 'Copiez ce texte :'), shareText);
                });
              }}
              title={t('product.shareOnInstagram', 'Partager sur Instagram')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </button>
          </div>

          {/* Caracteristiques */}
          <div className="product-specs">
            <h3>{t('product.characteristics')}</h3>

            {isVehicle ? (
              <>
                {/* Vehicle specs grid - style AutoScout */}
                <div className="vehicle-specs-grid">
                  {product.vehicle_make && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🏭</span>
                      <span className="vehicle-spec-label">{t('vehicle.make')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_make}</span>
                    </div>
                  )}
                  {product.vehicle_model && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🚗</span>
                      <span className="vehicle-spec-label">{t('vehicle.model')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_model}</span>
                    </div>
                  )}
                  {product.vehicle_year && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">📅</span>
                      <span className="vehicle-spec-label">{t('vehicle.year')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_year}</span>
                    </div>
                  )}
                  {product.vehicle_mileage != null && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🛣️</span>
                      <span className="vehicle-spec-label">{t('vehicle.mileage')}</span>
                      <span className="vehicle-spec-value">{Number(product.vehicle_mileage).toLocaleString()} km</span>
                    </div>
                  )}
                  {product.vehicle_fuel && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">⛽</span>
                      <span className="vehicle-spec-label">{t('vehicle.fuel')}</span>
                      <span className="vehicle-spec-value">{t('vehicle.fuel' + product.vehicle_fuel.charAt(0).toUpperCase() + product.vehicle_fuel.slice(1))}</span>
                    </div>
                  )}
                  {product.vehicle_transmission && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">⚙️</span>
                      <span className="vehicle-spec-label">{t('vehicle.transmission')}</span>
                      <span className="vehicle-spec-value">{t('vehicle.transmission' + product.vehicle_transmission.charAt(0).toUpperCase() + product.vehicle_transmission.slice(1))}</span>
                    </div>
                  )}
                  {product.vehicle_power_cv && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🐎</span>
                      <span className="vehicle-spec-label">{t('vehicle.power')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_power_cv} CV</span>
                    </div>
                  )}
                  {product.vehicle_displacement && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🔧</span>
                      <span className="vehicle-spec-label">{t('vehicle.displacement')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_displacement} cm³</span>
                    </div>
                  )}
                  {product.vehicle_doors && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🚪</span>
                      <span className="vehicle-spec-label">{t('vehicle.doors')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_doors}</span>
                    </div>
                  )}
                  {product.vehicle_seats && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">💺</span>
                      <span className="vehicle-spec-label">{t('vehicle.seats')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_seats}</span>
                    </div>
                  )}
                  {product.vehicle_drive && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🔄</span>
                      <span className="vehicle-spec-label">{t('vehicle.drive')}</span>
                      <span className="vehicle-spec-value">{t('vehicle.drive' + (product.vehicle_drive === '4x4' ? '4x4' : product.vehicle_drive.charAt(0).toUpperCase() + product.vehicle_drive.slice(1)))}</span>
                    </div>
                  )}
                  {product.color && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🎨</span>
                      <span className="vehicle-spec-label">{t('vehicle.exteriorColor')}</span>
                      <span className="vehicle-spec-value">{product.color}</span>
                    </div>
                  )}
                  {product.vehicle_color_interior && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">🪑</span>
                      <span className="vehicle-spec-label">{t('vehicle.interiorColor')}</span>
                      <span className="vehicle-spec-value">{product.vehicle_color_interior}</span>
                    </div>
                  )}
                  {product.vehicle_inspection_date && (
                    <div className="vehicle-spec-item">
                      <span className="vehicle-spec-icon">📋</span>
                      <span className="vehicle-spec-label">{t('vehicle.inspection')}</span>
                      <span className="vehicle-spec-value">{new Date(product.vehicle_inspection_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                {/* Condition */}
                <table className="specs-table" style={{ marginTop: '12px' }}>
                  <tbody>
                    <tr>
                      <td className="spec-label">{t('common.condition')}</td>
                      <td className="spec-value">{conditionLabels[product.condition] || product.condition}</td>
                    </tr>
                    {product.vehicle_vin && (
                      <tr>
                        <td className="spec-label">{t('vehicle.vin')}</td>
                        <td className="spec-value">{product.vehicle_vin}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            ) : (
              <table className="specs-table">
                <tbody>
                  {product.category_name && (
                    <tr>
                      <td className="spec-label">{t('common.category')}</td>
                      <td className="spec-value">{t('categories.' + product.category_slug, product.category_name)}</td>
                    </tr>
                  )}
                  {product.brand && (
                    <tr>
                      <td className="spec-label">{t('common.brand')}</td>
                      <td className="spec-value">{product.brand}</td>
                    </tr>
                  )}
                  {product.model && (
                    <tr>
                      <td className="spec-label">{t('common.model')}</td>
                      <td className="spec-value">{product.model}</td>
                    </tr>
                  )}
                  {product.size && (
                    <tr>
                      <td className="spec-label">{t('common.size')}</td>
                      <td className="spec-value">{product.size}</td>
                    </tr>
                  )}
                  {product.color && (
                    <tr>
                      <td className="spec-label">{t('common.color')}</td>
                      <td className="spec-value">{product.color}</td>
                    </tr>
                  )}
                  {product.material && (
                    <tr>
                      <td className="spec-label">{t('common.material')}</td>
                      <td className="spec-value">{product.material}</td>
                    </tr>
                  )}
                  {product.year && (
                    <tr>
                      <td className="spec-label">{t('common.year')}</td>
                      <td className="spec-value">{product.year}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="spec-label">{t('common.condition')}</td>
                    <td className="spec-value">{conditionLabels[product.condition] || product.condition}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Description */}
          <div className="product-description">
            <h3>{t('product.description')}</h3>
            <p>{product.description}</p>
            {translatedTitle && (
              <div className="translated-block">
                <h4 className="translated-title">{translatedTitle}</h4>
                <p>{translatedDesc}</p>
              </div>
            )}
            <button
              className={`btn-translate ${translatedTitle ? 'active' : ''}`}
              onClick={handleTranslate}
              disabled={translating}
            >
              {translating ? '⏳' : '🌐'} {translatedTitle ? t('product.showOriginal') : t('product.translate')}
            </button>
          </div>

          {/* Livraison */}
          {(product.shipping_available || product.local_pickup) && (
            <div className="product-shipping">
              <h3>{t('product.shipping')}</h3>
              <div className="shipping-options">
                {product.shipping_available && (
                  <div className="shipping-option available">
                    <span className="icon">📦</span>
                    <span>{t('product.shippingPostal')}{product.shipping_price > 0 ? ` - ${parseFloat(product.shipping_price).toFixed(2)} CHF` : ''}</span>
                  </div>
                )}
                {product.local_pickup && (
                  <div className="shipping-option available">
                    <span className="icon">🤝</span>
                    <span>{t('product.shippingHand')}{product.city && ` - ${product.city.trim()}`}</span>
                  </div>
                )}
              </div>
              <a
                href="https://www.post.ch/fr/expedier-des-colis/colis-suisse"
                target="_blank"
                rel="noopener noreferrer"
                className="post-help-link"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/Die_Post_Logo.svg/200px-Die_Post_Logo.svg.png"
                  alt="La Poste Suisse"
                  className="post-logo"
                />
                <span>{t('product.shippingHelp')}</span>
              </a>
            </div>
          )}

          {/* Localisation */}
          {(product.street || product.city || product.postal_code) && (
            <div className="product-location">
              <h3>{t('product.location')}</h3>
              <p>📍 {[product.street, product.postal_code, product.city].filter(Boolean).join(', ')}</p>
            </div>
          )}

          {/* Vendeur */}
          <div className="product-seller">
            <h3>{t('product.sellerLabel')}</h3>
            <div className="seller-info">
              <div className="seller-main">
                {product.seller_image ? (
                  <img
                    src={`/uploads/${product.seller_image}`}
                    alt={product.seller_username}
                    className="seller-avatar"
                  />
                ) : (
                  <div className="seller-avatar-placeholder">
                    {(product.seller_username || 'V').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="seller-details">
                  <span className="seller-name">{product.seller_username || 'Vendeur'}</span>
                  {sellerStats && sellerStats.total_reviews > 0 ? (
                    <div className="seller-rating-info">
                      <StarRating
                        rating={parseFloat(sellerStats.average_rating)}
                        size="small"
                        showCount={true}
                        count={sellerStats.total_reviews}
                      />
                    </div>
                  ) : (
                    <span className="no-reviews-yet">{t('product.newSeller')}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="seller-actions">
              {user && user.userId !== product.user_id && (
                <button className="btn-contact" onClick={handleContact}>
                  {t('product.contactSeller')}
                </button>
              )}
              {sellerStats && sellerStats.total_reviews > 0 && (
                <button
                  className="btn-see-reviews"
                  onClick={() => setShowReviews(!showReviews)}
                >
                  {showReviews ? t('product.hideReviews') : t('product.showReviews')}
                </button>
              )}
            </div>
            {showReviews && (
              <div className="seller-reviews-section">
                <SellerReviews sellerId={product.user_id} />
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="product-stats">
            <span>{t('product.views', { count: product.views || 0 })}</span>
            <span>•</span>
            <span>{t('product.favoritesCount', { count: product.favorites_count || 0 })}</span>
          </div>
        </div>
      </div>

      {/* Modal Zoom plein ecran */}
      {showZoom && currentImage && (
        <div
          className="fullscreen-zoom"
          onClick={handleZoomClose}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <button className="fullscreen-close" onClick={(e) => { e.stopPropagation(); setShowZoom(false); }}>✕</button>

          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {/* Fleche gauche */}
            {images.length > 1 && (
              <button className="fullscreen-nav fullscreen-prev" onClick={handlePrevImage}>
                ‹
              </button>
            )}

            {/* Image principale */}
            <img
              src={currentImage}
              alt={product.title}
              className="fullscreen-image"
              draggable="false"
            />

            {/* Fleche droite */}
            {images.length > 1 && (
              <button className="fullscreen-nav fullscreen-next" onClick={handleNextImage}>
                ›
              </button>
            )}
          </div>

          {/* Points indicateurs */}
          {images.length > 1 && (
            <div className="fullscreen-dots">
              {images.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                />
              ))}
            </div>
          )}

          {/* Thumbnails en bas */}
          {images.length > 1 && (
            <div className="fullscreen-thumbnails">
              {images.map((img, index) => (
                <div
                  key={index}
                  className={`fullscreen-thumb ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                >
                  <img src={`/uploads/thumbnails/${img}`} alt={`Photo ${index + 1}`} draggable="false" loading="lazy" onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${img}`; }} />
                </div>
              ))}
            </div>
          )}

          {/* Compteur */}
          <div className="fullscreen-counter">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}

      {/* Modal contact vendeur */}
      {showContactModal && (
        <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="contact-modal-header">
              <h3>{t('product.contactSellerTitle', { name: product.seller_username || t('product.sellerLabel') })}</h3>
              <button className="btn-close-modal" onClick={() => setShowContactModal(false)}>×</button>
            </div>
            <div className="contact-modal-product">
              {product.images && product.images.length > 0 && (
                <img src={`/uploads/thumbnails/${product.images[0]}`} alt="" onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${product.images[0]}`; }} />
              )}
              <div>
                <span className="modal-product-title">{product.title}</span>
                <span className="modal-product-price">{parseFloat(product.price).toFixed(2)} CHF</span>
              </div>
            </div>
            <form onSubmit={handleSendMessage}>
              <textarea
                placeholder={t('product.messagePlaceholder')}
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                rows={4}
                required
              />
              <button type="submit" disabled={sendingMessage || !contactMessage.trim()}>
                {sendingMessage ? t('product.sendingMessage') : t('product.sendMessage')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
